import DieRank from '@/types/die-rank';
import type { SwadeModConfigurationInterface } from '@/types/items';
import type { AuthoringDeclaration } from '@/appdata/container-fields';

/**
 * A container.skills modification may change the skill's RANK, its die (owner, 2026-10-08), as a skill
 * modification does: `step` the die type, `modify` the flat modifier, on the skill rank its record names.
 * It is applied and rolled back like an edge's skill bonus and never folded from the effects ledger: the
 * override rebuild resolves `rank` as a POWER's rank, a Novice-to-Legendary name. Craft's Target Skill
 * Property and the editor's Rank option write it; the API converts a Value written as a whole number.
 */
export const isSkillRankEffect = (configuration: Pick<SwadeModConfigurationInterface, 'path' | 'detail'> | null | undefined): boolean =>
    configuration?.path === 'container.skills' && configuration?.detail === 'rank';

/** What an author may do to a skill's rank, declared where it is applied; container-authoring.ts derives from it. */
export const SKILL_RANK_DECLARATION: AuthoringDeclaration = {
    operators: ['add', 'subtract'],
    valueKind: 'integer',
    valueTypes: ['step', 'modify'],
};

const WHOLE_NUMBER = /^\s*[+-]?\d+\s*$/;

/** A whole number, or one written as text as the API leaves one it could not convert; null for anything else. */
export function wholeNumber(value: unknown): number | null {
    const number = typeof value === 'number' ? value : (typeof value === 'string' && WHOLE_NUMBER.test(value) ? Number(value) : NaN);
    return Number.isInteger(number) ? number : null;
}

/**
 * The signed change one application makes: `add` raises by the value, `subtract` lowers by it. Null when
 * there is nothing to apply: any other operator, or a value that is not a whole number other than 0.
 */
export function skillRankAmount(configuration: SwadeModConfigurationInterface | null | undefined): number | null {
    const operator = configuration?.operator ?? null;
    if (operator !== 'add' && operator !== 'subtract') return null;
    const value = wholeNumber(configuration?.value);
    if (value === null || value === 0) return null;
    return operator === 'add' ? value : -value;
}

export interface SkillRankDie {
    rank: string;
    rankValue: number;
    modifier: number | null;
}

/**
 * A skill rank's die after a signed change, or null when the rank is not a die or the value type is
 * neither `step` nor `modify`. `modify` moves the flat modifier. `step` moves the die type through
 * DieRank, past d12 into the modifier as a legendary skill does, with two differences from
 * applySkillModification: the die never drops below d4, so a bonus rolled back never takes the player's
 * skill off the sheet; and below d12 a flat modifier is kept, where DieRank's own step-down discards it
 * and would eat another effect's bonus on the same skill. At d12 a positive modifier is still read as
 * above-d12 steps first: DieRank cannot tell those from a flat bonus.
 */
export function shiftSkillRank(rank: string, modifier: number | null, valueType: unknown, amount: number): SkillRankDie | null {
    if (valueType !== 'step' && valueType !== 'modify') return null;
    let die = new DieRank(rank).addModifier(modifier ?? 0);
    if (!die.isParsed()) return null;

    if (valueType === 'modify') {
        die.addModifier(amount);
    }
    else if (amount > 0) {
        die.addSteps(amount);
    }
    else if (die.getDieSides() === 12 && (die.getDieModifier() ?? 0) > 0) {
        die.subtractSteps(-amount);
    }
    else {
        const bare = new DieRank(die.getRankAsString() ?? '').subtractSteps(-amount);
        // DieRank marks a step below d4 as a -2 modifier on a d4; the floor drops it.
        const floored = (bare.getDieModifier() ?? 0) < 0 ? 'd4' : (bare.getRankAsString() ?? 'd4');
        die = new DieRank(floored).addModifier(modifier ?? 0);
    }

    return { rank: die.getRankAsString() ?? rank, rankValue: die.getValue(), modifier: die.getDieModifier() };
}
